import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Fade, Snackbar, Alert, Typography, IconButton, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { MoreHorizontal } from 'lucide-react';
import Auth from '../auth/Auth';
import HomeLimited from '../HomeLimited/HomeLimited';
import { Menu, MenuItem } from '@mui/material';

// Wallpaper images are in public folder, not imported
const wallpaperDay = '/images/wallpaper/wallpaperday-appartment.png';
const wallpaperNight = '/images/wallpaper/wallpapernight-appartment.png';

const FixedContainer = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
  // Add a subtle gradient background to enhance the blur effect
  backgroundImage: theme.palette.mode === 'dark' 
    ? 'linear-gradient(180deg, rgba(30, 30, 30, 0.5) 0%, rgba(0, 0, 0, 0) 100%)'
    : 'linear-gradient(180deg, rgba(240, 240, 240, 0.5) 0%, rgba(255, 255, 255, 0) 100%)',
  transition: 'background-color 0.3s ease',
  margin: 0,
  padding: 0,
  height: '100vh',
  minHeight: '100vh'
}));

const StyledWindowBar = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: 'transparent',
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  fontFamily: "'Inter', sans-serif",
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2.06, 2),
  height: '48px',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
  backdropFilter: 'blur(20px) saturate(180%)', // Aumentado el blur
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(0, 0, 0, 0.72)'
    : 'rgba(255, 255, 255, 0.72)',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  transition: 'background-color 0.3s ease',
}));

const BrandText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '20px',
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  position: 'relative',
  zIndex: 101,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}));

const HeaderMenu = React.memo(() => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    { text: 'Help & FAQ', url: 'https://directiveai.org/knowledge-base' },
    { text: 'Integration Guide', url: 'https://directiveai.org/docs' },
    { text: 'Support', url: 'https://directiveai.org/support' },
  ];

  return (
    <>
      <IconButton
        aria-label="menu"
        onClick={handleClick}
        sx={{ 
          color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000', // Updated for theme consistency
          position: 'relative',
          zIndex: 101
        }}
      >
        <MoreHorizontal />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.text}
            onClick={() => {
              window.open(item.url, '_blank');
              handleClose();
            }}
          >
            {item.text}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: 28,
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0px 1px 3px rgba(0, 0, 0, 0.5)'
    : '0px 8px 32px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.06)'
  }`,
  padding: theme.spacing(2, 3),
  '& .MuiAlert-icon': {
    color: theme.palette.mode === 'dark' ? '#fff' : '#0071e3',
    opacity: 0.9
  },
  '& .MuiAlert-action': {
    padding: theme.spacing(0, 0, 0, 2),
    '& .MuiIconButton-root': {
      padding: 8,
      borderRadius: 20,
      color: theme.palette.mode === 'dark' ? '#fff' : '#0071e3',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.1)'
          : 'rgba(0, 113, 227, 0.1)'
      }
    }
  },
  '& .MuiAlert-message': {
    padding: theme.spacing(1, 0),
    fontSize: '0.95rem'
  }
}));

const ContentArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'authShowing',
})(({ theme, authShowing }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(0),
  paddingTop: '0', // Removemos el padding-top aquí
  backgroundColor: authShowing
    ? (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF')
    : 'transparent',
  
  // Scrollbar styling - macOS style
  scrollbarWidth: 'thin', // Firefox
  scrollbarColor: 'transparent transparent', // Firefox - inicialmente transparente
  
  '&::-webkit-scrollbar': {
    width: '8px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: '10px',
    transition: 'background-color 0.3s ease',
  },
  '&::-webkit-scrollbar-button': {
    display: 'none',
  },
  // Solo mostrar al hacer hover sobre el contenedor
  '&:hover': {
    scrollbarColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.15) transparent' 
      : 'rgba(0, 0, 0, 0.15) transparent',
  },
  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.15)' 
      : 'rgba(0, 0, 0, 0.15)',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.25)' 
      : 'rgba(0, 0, 0, 0.25)',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
  },
}));

const LimitedDashboard = ({ onLogin, backgroundType, onBackgroundChange }) => {
  const [, setUser] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [agtService, setAgtService] = useState(null);
  const [storedMessage, setStoredMessage] = useState(null);

  const auth = getAuth();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  // Función para obtener el wallpaper según la hora del día
  const getCurrentBackground = useCallback(() => {

    if (backgroundType === 'wallpaper') {
      const hour = new Date().getHours();
      const isDayTime = hour >= 6 && hour < 18;
      const wallpaper = isDayTime ? wallpaperDay : wallpaperNight;
      return wallpaper;
    }
    return null;
  }, [backgroundType]);

  // Calcular el background actual
  const currentBackground = useMemo(() => getCurrentBackground(), [getCurrentBackground]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setShowAuth(false);
        onLogin(currentUser, true);
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, [auth, onLogin]);

  const handleAuthClose = () => {
    setShowAuth(false);
    setAgtService(null);
    setStoredMessage(null);
  };

  const handleSendMessage = () => {
    // Implementation for sending messages
  };

  const handleAgtServiceClick = (service) => {
    setAgtService(service);
    setShowAuth(true);
    setAuthMode('login');
  };

  const handleAuthRequired = (mode = 'login') => {
    setShowAuth(true);
    setAuthMode(mode);
  };

  const handleLogin = (user, isAuthorized) => {
    setUser(user);
    setShowAuth(false);
    if (agtService) {
      setAgtService(null);
    }
    if (storedMessage) {
      handleSendMessage(storedMessage);
      setStoredMessage(null);
    }
    onLogin(user, isAuthorized);
  };

  // Nuevo manejador para el clic en el logo
  const handleLogoClick = () => {
    if (showAuth) {
      setShowAuth(false);
      setAgtService(null);
      setStoredMessage(null);
    }
  };

  return (
    <FixedContainer
      style={{
        backgroundImage: (() => {
          const bg = currentBackground;
          if (!bg) return 'none';
          return `url("${bg}")`;
        })(),
        backgroundColor: currentBackground
          ? 'transparent'
          : (backgroundType === 'system'
              ? (prefersDarkMode ? '#000000' : '#FFFFFF')
              : (backgroundType === 'light' ? '#FFFFFF' : '#000000'))
      }}
    >
      <StyledWindowBar>
        <TopBar>
          <BrandText onClick={handleLogoClick}>DirectiveAI</BrandText>
          <HeaderMenu />
        </TopBar>
        <ContentArea authShowing={showAuth}>
          <Fade in={true} timeout={300}>
            <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
              {showAuth ? (
                <Fade 
                  in={showAuth} 
                  timeout={500}
                  style={{ 
                    transitionDelay: showAuth ? '50ms' : '0ms',
                  }}
                >
                  <Box 
                    sx={{ 
                      mt: 2,
                      animation: showAuth ? 'subtleZoom 0.5s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
                      '@keyframes subtleZoom': {
                        '0%': {
                          opacity: 0,
                          transform: 'scale(0.95) translateY(10px)',
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'scale(1) translateY(0)',
                        },
                      },
                    }}
                  >
                    <Auth 
                      mode={authMode} 
                      onClose={handleAuthClose} 
                      onLogin={handleLogin}
                      onToggleMode={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
                    />
                  </Box>
                </Fade>
              ) : (
                <Box sx={{ flexGrow: 1, overflow: 'auto', width: '100%', position: 'relative', zIndex: 0 }}>
                  <HomeLimited 
                    onSendMessage={handleSendMessage} 
                    onAgtEduClick={() => handleAgtServiceClick('AGT Edu')}
                    onAgtUpsClick={() => handleAgtServiceClick('AGT Ups')}
                    onAuthRequired={handleAuthRequired}
                    backgroundType={backgroundType}
                    onBackgroundChange={onBackgroundChange}
                  />
                </Box>
              )}
            </Box>
          </Fade>
        </ContentArea>
      </StyledWindowBar>
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: 'transparent',
            padding: 0,
            minWidth: 'auto',
            boxShadow: 'none'
          }
        }}
      >
        <StyledAlert 
          onClose={() => setErrorMessage('')} 
          severity="error"
        >
          {errorMessage}
        </StyledAlert>
      </Snackbar>
    </FixedContainer>
  );
};

export default LimitedDashboard;